import { Injectable, NgZone, ApplicationRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Tolgee, FormatSimple, TolgeeInstance } from '@tolgee/web';
import { InContextTools } from '@tolgee/web/tools';
import { environment } from '@env/environment';
import { HashMap } from '@core/translation/translation-models';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import * as deTranslations from '../../../../translations/de.json';
import * as enTranslations from '../../../../translations/en.json';
import * as deInlandPort from '../../../../translations/inland_port/de.json';
import * as enInlandPort from '../../../../translations/inland_port/en.json';

/** Tolgee namespace holding the global inland-port (Schiff) overrides. */
const INLAND_PORT_NS = 'inland_port';
/** Tolgee namespace holding the current tenant's per-key custom overrides. */
const TENANT_OVERRIDE_NS = 'tenant_override';
const INLAND_PORT_PRODUCT_TYPE = 'inland_port';
/** Languages with a `bcm_custom_language` override column. */
const OVERRIDE_LANGUAGES = ['de', 'en'];
/** Fallback namespace chains: when a key is missing in the requested ns, these are tried in order. */
const DEFAULT_FALLBACK_NS = [''];
const INLAND_PORT_FALLBACK_NS = [INLAND_PORT_NS, ''];

/** Placeholder keys from environment.local.template must not trigger live Tolgee fetches (401 in dev). */
function resolveTolgeeLiveApiKey(raw: string | undefined): string | undefined {
    if (!raw?.trim()) {
        return undefined;
    }

    const key = raw.trim();

    if (key.startsWith('<') || key.includes('{{')) {
        return undefined;
    }

    return key;
}

function lookupBundledStaticTranslation(key: string, language: string): string | undefined {
    const root = language === 'en' ? enTranslations : deTranslations;
    const parts = key.split('.');
    let node: unknown = root;

    for (const part of parts) {
        if (typeof node !== 'object' || node === null || !(part in (node as HashMap))) {
            return undefined;
        }

        node = (node as HashMap)[part];
    }

    return typeof node === 'string' ? node : undefined;
}

function setNested(target: HashMap, dottedKey: string, value: unknown): void {
    const parts = dottedKey.split('.');
    let cursor = target;
    for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (typeof cursor[part] !== 'object' || cursor[part] === null) {
            cursor[part] = {};
        }
        cursor = cursor[part];
    }
    cursor[parts[parts.length - 1]] = value;
}

@Injectable({
    providedIn: 'root'
})
export class TolgeeService {
    private static instance: TolgeeService;
    private tolgee: TolgeeInstance;
    private initialized = false;

    /**
     * Namespace every lookup is routed through. Undefined before a tenant is selected
     * (default namespace only); `tenant_override` once a tenant is applied, so per-tenant
     * overrides win and unmatched keys fall back via the configured fallback chain.
     */
    private lookupNs: string | undefined = undefined;

    private readonly _language$ = new BehaviorSubject<string>('de');

    /** Emits whenever Tolgee's active language changes (including after `run()`). */
    readonly language$: Observable<string> = this._language$.asObservable();

    constructor(
        private ngZone: NgZone,
        private http: HttpClient,
        private applicationRef: ApplicationRef,
    ) {
        this.ngZone.runOutsideAngular(() => {
            const tolgeeBuilder = Tolgee()
                .use(FormatSimple());

            const liveApiKey = resolveTolgeeLiveApiKey(environment.tolgeeApiKey);

            if (liveApiKey) {
                tolgeeBuilder.use(InContextTools());
            }

            this.tolgee = tolgeeBuilder.init({
                language: 'de',
                apiUrl: liveApiKey ? environment.tolgeeApiUrl : undefined,
                apiKey: liveApiKey,
                // Default fallback chain; switched per product type in applyTenant().
                fallbackNs: DEFAULT_FALLBACK_NS,
                staticData: {
                    de: deTranslations,
                    en: enTranslations,
                    [`de:${INLAND_PORT_NS}`]: deInlandPort,
                    [`en:${INLAND_PORT_NS}`]: enInlandPort,
                    // tenant_override records start empty; filled per tenant in applyTenant().
                    [`de:${TENANT_OVERRIDE_NS}`]: {},
                    [`en:${TENANT_OVERRIDE_NS}`]: {}
                }
            });
        });

        TolgeeService.instance = this;
    }

    static translate(key: string, params?: HashMap, defaultValue?: string): string {
        if (!TolgeeService.instance) {
            return defaultValue ?? key;
        }
        return TolgeeService.instance.translate(key, params, defaultValue);
    }

    static getLanguage(): string {
        return TolgeeService.instance?.getLanguage() || 'de';
    }

    async run(): Promise<void> {
        if (!this.initialized) {
            await this.ngZone.runOutsideAngular(() => this.tolgee.run());
            this.initialized = true;
            // Remote/static layers finish loading inside run(); refresh impure translate pipes.
            this.ngZone.run(() => {
                this._language$.next(this.getLanguage());
                this.applicationRef.tick();
            });
        }
    }

    translate(key: string, params?: HashMap, defaultValue?: string): string {
        // The translate pipe also receives raw table cell values (null, numbers) that are no keys.
        if (typeof key !== 'string' || !key) {
            return defaultValue ?? (key == null ? '' : String(key));
        }

        const bundledDefault = defaultValue ?? lookupBundledStaticTranslation(key, this.getLanguage());
        const result = this.tolgee.t({
            key,
            defaultValue: bundledDefault,
            params,
            ns: this.lookupNs,
        });

        if (result && result !== key) {
            return result;
        }

        if (bundledDefault) {
            const fromBundled = this.tolgee.t({
                key,
                defaultValue: bundledDefault,
                params,
                ns: '',
            });

            if (fromBundled && fromBundled !== key) {
                return fromBundled;
            }

            return bundledDefault;
        }

        return result ?? key;
    }

    /**
     * Applies the current tenant's translation layers:
     *  - routes lookups through the `tenant_override` namespace (per-key custom overrides),
     *  - switches the fallback chain so inland-port tenants resolve Boot→Schiff via the
     *    `inland_port` namespace before the default.
     * The tenant_override records are fully replaced, so the previous tenant's overrides
     * are discarded automatically (Tolgee `addStaticData` replaces a record).
     */
    async applyTenant(productType: string, tenantId: number): Promise<void> {
        const inlandPort = productType === INLAND_PORT_PRODUCT_TYPE;

        const fetched = await Promise.all(
            OVERRIDE_LANGUAGES.map(async (language) => ({
                language,
                overrides: await this.fetchCustomOverrides(tenantId, language)
            }))
        );

        this.ngZone.runOutsideAngular(() => {
            // `updateOptions` re-applies the init staticData and discards prior runtime
            // `addStaticData`, so it MUST run before we add this tenant's override records.
            this.tolgee.updateOptions({ fallbackNs: inlandPort ? INLAND_PORT_FALLBACK_NS : DEFAULT_FALLBACK_NS });
            const records: HashMap = {};
            for (const { language, overrides } of fetched) {
                records[`${language}:${TENANT_OVERRIDE_NS}`] = this.toNestedOverrides(overrides);
            }
            this.tolgee.addStaticData(records);
        });

        this.lookupNs = TENANT_OVERRIDE_NS;
        this.ngZone.run(() => this._language$.next(this.getLanguage()));
    }

    /** Clears tenant-specific translation layers (e.g. on logout / tenant deselect). */
    resetTenant(): void {
        // `updateOptions` re-applies the init staticData, which restores the empty
        // tenant_override records — i.e. the previous tenant's overrides are dropped.
        this.ngZone.runOutsideAngular(() =>
            this.tolgee.updateOptions({ fallbackNs: DEFAULT_FALLBACK_NS })
        );
        this.lookupNs = undefined;
        this.ngZone.run(() => this._language$.next(this.getLanguage()));
    }

    private async fetchCustomOverrides(tenantId: number, language: string): Promise<HashMap<string>> {
        try {
            const url = `${environment.endpoints.bcm.api}${tenantId}/i18n/custom/${language}`;
            return (await firstValueFrom(this.http.get<HashMap<string>>(url))) ?? {};
        } catch {
            return {};
        }
    }

    /** Expands a flat `{ "a.b.c": "value" }` override map into a nested object, dropping empties. */
    private toNestedOverrides(overrides: HashMap<string>): HashMap {
        const data: HashMap = {};
        for (const [key, value] of Object.entries(overrides)) {
            if (value != null && value !== '') {
                setNested(data, key, value);
            }
        }
        return data;
    }

    getInstance(): TolgeeInstance {
        return this.tolgee;
    }

    async changeLanguage(language: string): Promise<void> {
        await this.ngZone.runOutsideAngular(() => this.tolgee.changeLanguage(language));
        this.ngZone.run(() => {
            this._language$.next(this.getLanguage());
            this.applicationRef.tick();
        });
    }

    getLanguage(): string {
        return this.tolgee.getLanguage() || 'de';
    }

    /**
     * Maps Tolgee language codes to a locale string registered via `registerLocaleData`
     * for Angular `DatePipe` / `LOCALE_ID`-style formatting.
     */
    getAngularDateLocale(lang = this.getLanguage()): string {
        const normalized = (lang || 'de').toLowerCase().replace(/_/g, '-');
        if (normalized.startsWith('en')) {
            return 'en-US';
        }
        if (normalized === 'de-ch') {
            return 'de-CH';
        }
        if (normalized.startsWith('de')) {
            return 'de-DE';
        }
        return 'de-DE';
    }
}
